/**
 * Drive → PDApps: this org's PDApps shared to the viewer through a share link that
 * names them (#1484). A link share means "run it, don't read its internals", so a
 * card offers Run and nothing else — no Open as project, Show in drive or View
 * files. An app the viewer can also open in their own drive is not listed here:
 * the server leaves it out, and its full card is in the sections above.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Link2, Play } from 'lucide-react'
import { parseMiniappIcon, parseMiniappIconRev } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { MiniappIcon, Tooltip, useToast } from '@papan/core-ui/ui'
import { openLinkApp, useSharedLinkApps, type PdappEntry } from './appsData'

export function LinkAppSection({ filter = '', enabled = true }: {
  filter?: string
  /** The drive deck is kept mounted while hidden — read only while it is shown. */
  enabled?: boolean
}) {
  const { t } = useTranslation('files')
  const qc = useQueryClient()
  const toast = useToast()
  const links = useSharedLinkApps(true, enabled)
  const q = filter.trim().toLowerCase()
  const entries = (links.data ?? []).filter(e => !q || e.name.toLowerCase().includes(q))
  if (entries.length === 0) return null
  const run = (e: PdappEntry) => { openLinkApp(qc, e).catch(() => toast.error(t('appsWindow.linkUnavailable'))) }
  return (
    <section className="mb-8" data-testid="projects-section-link">
      <h2 className="ws-home-heading mb-3 flex items-center gap-1.5">
        <Link2 size={14} />
        {t('appsWindow.sections.sharedByLink')}
        <span className="themed-text-muted font-normal">{entries.length}</span>
      </h2>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3">
        {entries.map(e => (
          <div
            key={e.key}
            onClick={() => run(e)}
            role="button"
            tabIndex={0}
            onKeyDown={(ev) => { if (ev.key === 'Enter') run(e) }}
            className="ws-project-card group text-left cursor-pointer"
            data-testid="link-app-card"
          >
            <div className="flex items-center gap-3">
              <div className="shrink-0 mx-1.5">
                {/* The icon SPEC only — an image icon is fetched by the app's folder id, which a link card never has. */}
                <MiniappIcon spec={parseMiniappIcon(e.icon)} name={e.name} size={40} refId={null} rev={parseMiniappIconRev(e.icon)} />
              </div>
              <div className="min-w-0 flex-1">
                <span className="block text-sm font-medium themed-text truncate">{e.name}</span>
                <span className="block text-[10px] themed-text-muted truncate mt-0.5">{t('appsWindow.viaLink')}</span>
              </div>
              <Tooltip always content={t('driveHome.projectsPage.runApp', { defaultValue: 'Run app' })}>
                <button
                  onClick={(ev) => { ev.stopPropagation(); run(e) }}
                  className="ws-card-iconbtn"
                  aria-label={t('driveHome.projectsPage.runApp', { defaultValue: 'Run app' })}
                >
                  <Play size={16} />
                </button>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
